import { useState, useEffect, useCallback } from 'react';
import { useAuth, type PresenceStatus } from '../../context/AuthContext';
import { useSettings } from '../SettingsContext';
import { PRESENCE_STATUS_CONFIG } from './presenceStatusConfig';
import { getUserHandle } from './utils';

export function useUserMenu() {
  const { user, logout, openProfile, getPresenceStatus, updatePresenceStatus } = useAuth();
  const { settings, setSettings } = useSettings();
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [settingsTab, setSettingsTab] = useState<'appearance' | 'notifications'>('appearance');
  const [isKeyboardShortcutsOpen, setIsKeyboardShortcutsOpen] = useState(false);
  const [presenceStatus, setPresenceStatus] = useState<PresenceStatus>('available');

  // Initialize presence status from user
  useEffect(() => {
    if (user) {
      setPresenceStatus(getPresenceStatus());
    }
  }, [user, getPresenceStatus]);

  const handlePresenceStatusChange = useCallback(async (status: PresenceStatus) => {
    // Optimistically update UI immediately for better responsiveness
    setPresenceStatus(status);
    // Update in background without blocking UI
    updatePresenceStatus(status).catch((error) => {
      console.error('Failed to update presence status:', error);
      // Revert on error
      setPresenceStatus(getPresenceStatus());
    });
  }, [updatePresenceStatus, getPresenceStatus]);

  const handleStatusChange = useCallback((value: string) => {
    handlePresenceStatusChange(value as PresenceStatus);
  }, [handlePresenceStatusChange]);

  const handleSettingsClick = useCallback(() => {
    setSettingsTab('appearance');
    setIsSettingsOpen(true);
  }, []);

  const handleNotificationsClick = useCallback(() => {
    setSettingsTab('notifications');
    setIsSettingsOpen(true);
  }, []);

  const handleKeyboardShortcutsClick = useCallback(() => {
    setIsKeyboardShortcutsOpen(true);
  }, []);

  const handleThemeToggle = useCallback(() => {
    const currentTheme = settings.theme;
    const nextTheme = currentTheme === 'dark' ? 'light' : currentTheme === 'light' ? 'system' : 'dark';
    setSettings((prev) => ({ ...prev, theme: nextTheme }));
  }, [settings.theme, setSettings]);

  const statusColorClass = PRESENCE_STATUS_CONFIG[presenceStatus]?.colorClass || 'bg-muted-foreground';

  const themeLabel = settings.theme === 'dark' ? 'Dark' : settings.theme === 'light' ? 'Light' : 'System';
  const userHandle = getUserHandle(user);

  return {
    user,
    presenceStatus,
    statusColorClass,
    handleStatusChange,
    openProfile,
    handleSettingsClick,
    handleNotificationsClick,
    handleKeyboardShortcutsClick,
    handleThemeToggle,
    themeLabel,
    logout,
    userHandle,
    isSettingsOpen,
    setIsSettingsOpen,
    settingsTab,
    isKeyboardShortcutsOpen,
    setIsKeyboardShortcutsOpen,
  };
}







